import {
  rand,
  randPastDate,
  randRecentDate,
  randBoolean,
  randBetweenDate,
} from '@ngneat/falso'
import {
  pageable,
  sortable,
  getQueryParamValue,
  createErrorResponse,
} from './utils.js'
import { Model, Factory, hasMany, belongsTo } from 'miragejs'
import type { ModelInstance, ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from './server.js'
import type { SchemaAttributes } from '../types.js'
import {
  ClientStatementDateFilter,
  ClientStatementDateFieldKeyMap,
  type ClientStatementDateFilters,
} from '@/models/clientStatement.js'
import type { InvoiceRowData } from '@/composables/api/Invoice.js'
import { formatDate, formatToDateTimeISO } from '@/utils/date/conversion.js'
import { getDateTimeTimestamp, getEndOfPeriod } from '@/utils/date/getter.js'
import { isAfter, isBefore, isValidDate } from '@/utils/date/validation.js'
import { toNumber } from '@/utils/number.js'

export type ClientStatementData = SchemaAttributes<'ClientStatement'>

export const model = Model.extend({
  invoices: hasMany('Invoice'),
  prepayments: hasMany('Prepayment'),
  patients: hasMany('Patient'),
  client: belongsTo('Client'),
  department: belongsTo('Department'),
}) as ModelDefinition<ClientStatementData>

export const factory = Factory.extend<ClientStatementData>({
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randBetweenDate({
      from: this.created as string,
      to: randRecentDate(),
    }).toISOString()
  },
  name() {
    return `Client statement - ${formatDate(this.created!.toString(), 'dd-MM-yyyy')}`
  },
  start_date() {
    return randPastDate().toISOString()
  },
  end_date() {
    return randBetweenDate({
      from: this.start_date as string,
      to: randRecentDate(),
    }).toISOString()
  },
  statement_type() {
    return rand([
      'CLIENT_STATEMENT_TYPE_SUMMARY',
      'CLIENT_STATEMENT_TYPE_DETAILED',
    ])
  },
  date_filter() {
    return rand(Object.values(ClientStatementDateFilter))
  },
  archived() {
    return randBoolean()
  },

  afterCreate(clientStatement, server) {
    const client = server.create(
      'client',
      'withPrimaryOwnerAsInvoicingClient',
      'withRandomCriticalNotes',
      'withPhoneNumbers',
      'withTags',
      'withSecondaryOwner',
      'withAlternativePayer',
      'withPatients',
    )
    const patients = client
      ? (server as MockServer).schema
          .all('patient')
          .models.filter((patient) => patient.clientId === client.id)
      : []

    clientStatement.update({
      invoices: [],
      department: server.schema.first('department'),
      client,
      patients,
    })
  },
})

export function routes(server: MockServer) {
  server.resource('clientStatement')

  server.get(
    '/client_statements/',
    function (this: MirageServer, schema, request) {
      const clientStatement = schema.all('clientStatement')
      const sorted = sortable(clientStatement, request)

      // then paginate
      const { data, meta } = pageable(sorted, request)

      // and finally construct the response
      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.get(
    '/client_statements/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const clientStatement = schema.find('clientStatement', id)

      if (!clientStatement) {
        return createErrorResponse({
          status: 404,
          headers: { 'Content-Type': 'application/json' },
          errors: [
            {
              detail: 'Client statement not found.',
            },
          ],
        })
      }

      return clientStatement
    },
  )

  server.get(
    '/client_statements/invoices',
    function (this: MirageServer, schema, request) {
      const clientIdParam = getQueryParamValue(
        request?.queryParams?.['filter[client]'],
      )
      const clientStatementIdParam = getQueryParamValue(
        request?.queryParams?.['filter[client_statement_id]'],
      )

      // Get all Invoices that belong to the Client Statement
      const invoices = schema.where('invoice', {
        clientId: clientIdParam,
        ...(clientStatementIdParam
          ? { clientStatementId: clientStatementIdParam }
          : {}),
      })

      // 1) Sort - add default sort by `invoice_date` ascending
      // Sorts Invoices based on the "Date Filter" param.
      const dateFilterParam =
        getQueryParamValue(request?.queryParams?.['filter[date_filter]']) ||
        ClientStatementDateFilter.InvoiceDueDate

      if (
        'filter[date_filter]' in request.queryParams &&
        !Object.values(ClientStatementDateFilter).includes(
          dateFilterParam as ClientStatementDateFilters,
        )
      ) {
        return createErrorResponse({
          status: 400,
          errors: [
            {
              code: 'invalid_choice',
              detail: `Select a valid choice. ${dateFilterParam} is not one of the available choices.`,
              pointer: '/data/attributes/date_filter',
            },
          ],
        })
      }

      if ('filter[date_filter]' in request.queryParams && !dateFilterParam) {
        return createErrorResponse({
          status: 400,
          errors: [
            {
              code: 'invalid',
              detail: 'missing value for query parameter filter[date_filter]',
              pointer: '/data',
            },
          ],
        })
      }

      const dateField =
        ClientStatementDateFieldKeyMap[
          dateFilterParam as ClientStatementDateFilters
        ]
      const sorted = invoices.sort((a, b) => {
        const aInvoiceDate = getDateTimeTimestamp(a?.attrs?.[dateField] || '')
        const bInvoiceDate = getDateTimeTimestamp(b?.attrs?.[dateField] || '')

        return aInvoiceDate - bInvoiceDate
      })

      // 2) Filter
      let filtered = sorted

      // 2.1) Date filtering.
      // Filters Invoices based on the "Date Filter" param.
      const startDateParam = getQueryParamValue(
        request?.queryParams?.['filter[start_date]'],
      )
      const endDateParam = getQueryParamValue(
        request?.queryParams?.['filter[end_date]'],
      )

      if (startDateParam || endDateParam) {
        const startDate =
          startDateParam && typeof startDateParam === 'string'
            ? formatToDateTimeISO(startDateParam)
            : null
        const endDate =
          endDateParam && typeof endDateParam === 'string'
            ? getEndOfPeriod(endDateParam, 'day')
            : null

        filtered = filtered.filter((invoice) => {
          const invoiceDate =
            formatToDateTimeISO(invoice.attrs[dateField] || '') || ''
          if (!isValidDate(invoiceDate)) {
            return false
          }

          const startComparison =
            !startDate || !isBefore(invoiceDate, startDate)
          const endComparison = !endDate || !isAfter(invoiceDate, endDate)

          return startComparison && endComparison
        })
      }

      // 2.2) Patients filtering.
      // Filters out Invoices that don't have any of the patients in their
      // respective Invoice Rows.
      const patientsFilterParam = getQueryParamValue(
        request?.queryParams['filter[patients]'],
      )

      if ('filter[patients]' in request.queryParams && !patientsFilterParam) {
        return createErrorResponse({
          status: 400,
          errors: [
            {
              code: 'invalid',
              detail: 'missing value for query parameter filter[patients]',
              pointer: '/data',
            },
          ],
        })
      }

      if ('filter[patients]' in request.queryParams && patientsFilterParam) {
        const patientIds = patientsFilterParam.split(',')
        const allAreWholeNumbers = patientIds.every((id: string) =>
          /^\d+$/.test(id),
        )

        if (!allAreWholeNumbers) {
          return createErrorResponse({
            status: 400,
            errors: [
              {
                code: 'invalid',
                detail: 'Enter a whole number.',
                pointer: '/data',
              },
            ],
          })
        }
      }

      const patientsFilterArray = patientsFilterParam?.split(',')
      if (patientsFilterArray?.length) {
        filtered = filtered.filter((invoice) => {
          const invoiceRowIds = invoice.attrs.rowIds
          const invoiceRows = invoiceRowIds?.map((id: string) =>
            schema.find('invoiceRow', id),
          ) as ModelInstance<Partial<InvoiceRowData> & { patientId?: string }>[]

          return invoiceRows.some((row) =>
            patientsFilterArray.includes(row.attrs.patientId?.toString() ?? ''),
          )
        })
      }

      // 3) Filter invoice payments based on current_balance
      const additionalFilters = getQueryParamValue(
        request?.queryParams?.['filter[additional_filters]'],
      )
      const isCurrentBalanceEnabled =
        JSON.parse(additionalFilters || '{}')?.current_balance === true

      if (!isCurrentBalanceEnabled && (startDateParam || endDateParam)) {
        const startDate =
          startDateParam && typeof startDateParam === 'string'
            ? formatToDateTimeISO(startDateParam)
            : null
        const endDate =
          endDateParam && typeof endDateParam === 'string'
            ? getEndOfPeriod(endDateParam, 'day')
            : null

        // Filter payments for each invoice based on date range
        filtered.models.forEach((invoice) => {
          // Get all payment IDs for this invoice
          const paymentIds = invoice.attrs.paymentIds || []
          let filteredPaymentsSum = 0

          // Filter payment IDs to only include those within the date range
          const filteredPaymentIds = paymentIds.filter((paymentId: string) => {
            const payment = schema.find('invoicePayment', paymentId)
            if (!payment) {
              return false
            }

            const paymentDate =
              formatToDateTimeISO(payment.attrs?.date_added || '') || ''
            if (!isValidDate(paymentDate)) {
              return false
            }

            const isWithinRange =
              (!startDate || !isBefore(paymentDate, startDate)) &&
              (!endDate || !isAfter(paymentDate, endDate))

            if (!isWithinRange) {
              filteredPaymentsSum += toNumber(payment.attrs.paid)
            }

            return isWithinRange
          })

          // Modify the payments attritubes being returned in the query,
          // without actually modifying their values in the model's database
          invoice.attrs.paymentIds = filteredPaymentIds
          invoice.attrs.total_paid =
            invoice.attrs.total_paid - filteredPaymentsSum
          invoice.attrs.due_sum =
            invoice.attrs.total_with_vat - invoice.attrs.total_paid
          invoice.attrs.due_sum_at_end_date = invoice.attrs.due_sum
          invoice.attrs.client_due_sum = invoice.attrs.due_sum
        })
      }

      // 4) Paginate
      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.post('/client_statements', function (db, request) {
    const requestBody = JSON.parse(request.requestBody)
    const attributes = requestBody.data.attributes

    return db.create('clientStatement', attributes)
  })

  server.patch('/client_statements/:id', async (schema, request) => {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const clientStatement = schema.find('clientStatement', id)

    if (!clientStatement) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Client Statement with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    await clientStatement.update(attrs.data.attributes)

    return {
      data: {
        id: clientStatement.id,
        type: 'ClientStatement',
        attributes: {
          name: clientStatement.attrs.name,
          start_date: clientStatement.attrs.start_date,
          end_date: clientStatement.attrs.end_date,
          statement_type: clientStatement.attrs.statement_type,
          date_filter: clientStatement.attrs.date_filter,
          archived: clientStatement.attrs.archived,
        },
      },
    }
  })

  server.get(
    '/client_statements/prepayments',
    function (this: MirageServer, schema, request) {
      const clientIdParam = getQueryParamValue(
        request?.queryParams?.['filter[client]'],
      )
      // Get all Prepayments that belong to the Client Statement
      const prepayments = schema.where('prepayment', {
        clientId: clientIdParam,
      })

      // Sort
      const sorted = prepayments.sort((a, b) => {
        const aInvoiceDate = getDateTimeTimestamp(a?.attrs?.date_added || '')
        const bInvoiceDate = getDateTimeTimestamp(b?.attrs?.date_added || '')

        return aInvoiceDate - bInvoiceDate
      })

      // Check for current_balance setting
      const additionalFilters = getQueryParamValue(
        request?.queryParams?.['filter[additional_filters]'],
      )
      const isCurrentBalanceEnabled =
        JSON.parse(additionalFilters || '{}')?.current_balance === true

      // 2) Filter based on current_balance setting
      let filtered = sorted

      if (isCurrentBalanceEnabled) {
        // For current_balance, include ALL prepayments for this client (no date filtering)
        filtered = sorted
      } else {
        // For normal date filtering, include Prepayments based on the "Start" and "End" date params.
        const startDateParam = getQueryParamValue(
          request?.queryParams?.['filter[start_date]'],
        )
        const endDateParam = getQueryParamValue(
          request?.queryParams?.['filter[end_date]'],
        )

        if (startDateParam || endDateParam) {
          const startDate =
            startDateParam && typeof startDateParam === 'string'
              ? formatToDateTimeISO(startDateParam)
              : null
          const endDate =
            endDateParam && typeof endDateParam === 'string'
              ? getEndOfPeriod(endDateParam, 'day')
              : null

          filtered = filtered.filter((invoice) => {
            const invoiceDate =
              formatToDateTimeISO(invoice.attrs.date_added || '') || ''
            if (!isValidDate(invoiceDate)) {
              return false
            }

            const startComparison =
              !startDate || !isBefore(invoiceDate, startDate)
            const endComparison = !endDate || !isAfter(invoiceDate, endDate)

            return startComparison && endComparison
          })
        }
      }

      return {
        ...this.serialize(filtered),
      }
    },
  )

  server.get(
    '/client_statements/:id/pdf',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const clientStatement = schema.find('clientStatement', id)

      if (!clientStatement) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Client Statement with id ${id} not found` }],
        })
      }

      return {
        data: {
          id: clientStatement.id,
          type: 'Base64Document',
          attributes: {
            content: btoa('Hello World!'),
            filename: `client-statement-${clientStatement.id}.pdf`,
            content_type: 'application/pdf',
          } satisfies SchemaAttributes<'Base64Document'>,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  server.createList('clientStatement', 2)

  // #ID 196: Generate Client for ClientStatement project
  // Making sure that the client has a phone number and that
  // `is_secondary_owners_phone_number` is set to false.
  const phoneNumber = server.create('phone', {
    is_secondary_owners_phone_number: false,
  })
  server.create(
    'client',
    'withSecondaryOwner',
    'withPatients',
    'withInvoices',
    'withPrepayments',
    {
      id: '196',
      phoneNumbers: [phoneNumber],
    },
  )
}
